import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiBookOpen, FiFileText, FiMail } from 'react-icons/fi';
import { useAuth } from '../hooks/useAuth';
import Logo from '../assets/logo';
import NotificationIcon from '../components/common/NotificationIcon';
import AppFooter from '../components/common/AppFooter';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import AskAiAssistant from '../components/common/AskAiAssistant';
import { administrationLabels } from '@vikar-experten/ask-ai-embedded/administration';
import './AdminLayout.css';

// Dashboard icon component for navigation
const DashboardIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"></rect>
    <rect x="14" y="3" width="7" height="7"></rect>
    <rect x="14" y="14" width="7" height="7"></rect>
    <rect x="3" y="14" width="7" height="7"></rect>
  </svg>
);

// Users icon
const UsersIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

// Customers icon
const CustomerIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

// Employees icon
const EmployeeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

// Assignments icon (shuffle — employees ↔ customers)
const AssignmentIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 3 21 3 21 8"></polyline>
    <line x1="4" y1="20" x2="21" y2="3"></line>
    <polyline points="21 16 21 21 16 21"></polyline>
    <line x1="15" y1="15" x2="21" y2="21"></line>
    <line x1="4" y1="4" x2="9" y2="9"></line>
  </svg>
);

// Reports icon
const ReportIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <path d="M14 2v6h6"></path>
    <path d="M16 13H8"></path>
    <path d="M16 17H8"></path>
    <path d="M10 9H8"></path>
  </svg>
);

// Settings icon
const SettingsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"></circle>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
  </svg>
);

// Menu icon for mobile toggle
const MenuIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);

// SystemIcon component
const SystemIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect>
    <rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect>
    <line x1="6" y1="6" x2="6.01" y2="6"></line>
    <line x1="6" y1="18" x2="6.01" y2="18"></line>
  </svg>
);

const AdminLayout = () => {
  const { currentUser, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const administration = administrationLabels(String(i18n.language || 'en').split('-')[0]);
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  // Support shares this layout for the three sections it owns; the rest of the
  // menu is not theirs to see.
  const isSupport = currentUser?.userType === 'support';

  // Close the mobile sidebar after navigating (selecting a menu item)
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;

    if (path.includes('/admin/dashboard')) return t('nav.admin.dashboard');
    if (path.includes('/admin/users')) return t('admin.pageTitle.users');
    if (path.includes('/admin/customers')) return t('nav.admin.customers');
    if (path.includes('/admin/assignments')) return t('nav.admin.assignments');
    // Exact segment match: '/admin/email' must not swallow '/admin/employees'
    // (nor the other way round) as the includes() checks below would.
    if (/^\/admin\/email(\/|$)/.test(path)) return t('nav.admin.emailDelivery');
    if (path.includes('/admin/employees')) return t('nav.admin.employees');
    if (path.includes('/admin/time-reports')) return t('nav.admin.timeReports');
    if (path.includes('/admin/invoicing')) return t('nav.admin.invoicing');
    if (path.includes('/admin/payroll')) return t('nav.admin.payroll');
    if (path.includes('/admin/integrations')) return t('nav.admin.integrations');
    if (path.includes('/admin/configuration')) return t('nav.admin.configuration');
    if (path.includes('/admin/settings')) return t('nav.admin.settings');
    if (path.includes('/admin/system')) return t('nav.admin.system');
    if (path.includes('/admin/profile')) return t('nav.admin.profile');
    if (path.includes('/admin/notification-preferences')) return t('admin.pageTitle.notificationPreferences');

    return t('admin.pageTitle.default');
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed', error);
    }
  };
  
  // Close sidebar when clicking outside on mobile
  const handleOverlayClick = () => {
    setIsSidebarOpen(false);
  };

  // The admin guide is a static page, built in Danish and English only.
  const adminGuideHref = `/guides/admin.${String(i18n.language || '').toLowerCase().startsWith('da') ? 'da' : 'en'}.html`;
  
  // User initials for avatar
  const getUserInitials = () => {
    if (!currentUser) return 'U';
    
    const firstName = currentUser.firstName || '';
    const lastName = currentUser.lastName || '';
    
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <div className={`admin-layout-container${location.pathname === '/admin/payroll' ? ' payroll-workspace-layout' : ''}`}>
      {/* Sidebar */}
      <aside 
        className="admin-sidebar" 
        style={{ '--sidebar-transform': isSidebarOpen ? '0' : '-100%' }}
      >
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <Logo height={34} />
            <div className="sidebar-logo-text">{t('common.appName')}</div>
          </div>
        </div>
        
        <div className="sidebar-content">
          <nav className="nav-menu">
            {/* A support user gets the Customers, Employees and Assignments
                sections and nothing else — the console they came from replaces
                the dashboard as their "home". AdminOnlyRoute enforces the same
                boundary on the routes, and the API on the endpoints; this is
                only what they see. */}
            {!isSupport && (
              <div className="nav-section">
                <div className="nav-section-title">{t('admin.section.main')}</div>
                <NavLink to="/admin/dashboard" className={({isActive}) => isActive ? "nav-item active" : "nav-item"}>
                  <DashboardIcon />
                  {t('nav.admin.dashboard')}
                </NavLink>
              </div>
            )}

            {isSupport && (
              <div className="nav-section">
                <div className="nav-section-title">{t('admin.section.main')}</div>
                <NavLink to="/support" className="nav-item">
                  <DashboardIcon />
                  {t('support.console.title')}
                </NavLink>
              </div>
            )}

            <div className="nav-section">
              <div className="nav-section-title">{t('admin.section.management')}</div>
              {!isSupport && (
                <NavLink to="/admin/users" className={({isActive}) => isActive ? "nav-item active" : "nav-item"}>
                  <UsersIcon />
                  {t('nav.admin.users')}
                </NavLink>
              )}
              <NavLink to="/admin/customers" className={({isActive}) => isActive ? "nav-item active" : "nav-item"}>
                <CustomerIcon />
                {t('nav.admin.customers')}
              </NavLink>
              <NavLink to="/admin/employees" className={({isActive}) => isActive ? "nav-item active" : "nav-item"}>
                <EmployeeIcon />
                {t('nav.admin.employees')}
              </NavLink>
              <NavLink to="/admin/assignments" className={({isActive}) => isActive ? "nav-item active" : "nav-item"}>
                <AssignmentIcon />
                {t('nav.admin.assignments')}
              </NavLink>
              {!isSupport && (
                <>
                  <NavLink to="/admin/time-reports" className={({isActive}) => isActive ? "nav-item active" : "nav-item"}>
                    <ReportIcon />
                    {t('nav.admin.timeReports')}
                  </NavLink>
                  <NavLink to="/admin/invoicing" className={({isActive}) => isActive ? "nav-item active" : "nav-item"}>
                    <ReportIcon />
                    {t('nav.admin.invoicing')}
                  </NavLink>
                  <NavLink to="/admin/payroll" className={({isActive}) => isActive ? "nav-item active" : "nav-item"}>
                    <ReportIcon />
                    {t('nav.admin.payroll')}
                  </NavLink>
                </>
              )}
            </div>

            <div className="nav-section">
              <div className="nav-section-title">{administration.administration}</div>
              {!isSupport && (
                <>
                  <NavLink to="/admin/integrations" className={({isActive}) => isActive ? "nav-item active" : "nav-item"}>
                    <SettingsIcon />
                    {administration.integrations}
                  </NavLink>
                  <NavLink to="/admin/settings" className={({isActive}) => isActive ? "nav-item active" : "nav-item"}>
                    <SettingsIcon />
                    {t('nav.admin.settings')}
                  </NavLink>
                  {currentUser?.isSuperAdmin === true && (
                    <NavLink to="/admin/configuration" className={({isActive}) => isActive ? "nav-item active" : "nav-item"}>
                      <SettingsIcon />
                      {t('nav.admin.configuration')}
                    </NavLink>
                  )}
                  <NavLink to="/admin/system" className={({isActive}) => isActive ? "nav-item active" : "nav-item"}>
                    <SystemIcon />
                    {administration.systemStatus}
                  </NavLink>
                </>
              )}
              {/* E-mail delivery is shared with support: they trigger sends too
                  (invitations, login links) and need to see whether those went
                  out. The API scopes their view to the rows they requested. */}
              <NavLink to="/admin/email" className={({isActive}) => isActive ? "nav-item active" : "nav-item"}>
                <FiMail />
                {t('nav.admin.emailDelivery')}
              </NavLink>
              {/* Static guide page, so a plain <a> in a new tab — never a route.
                  The mobile drawer closes on pathname change, which does not fire
                  for an external link, hence the explicit close. */}
              <a
                className="nav-item"
                href={adminGuideHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsSidebarOpen(false)}
              >
                <FiBookOpen />
                {t('common.userGuide')}
              </a>
              {/* Legal documents open in a new tab for the same reason as the
                  guide: they are reference material, not an app screen. */}
              <a
                className="nav-item"
                href="/legal"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsSidebarOpen(false)}
              >
                <FiFileText />
                {t('legal.navLabel')}
              </a>
            </div>
          </nav>
        </div>
        
        <div className="sidebar-footer">
          {t('common.copyright', { year: new Date().getFullYear() })}
        </div>
      </aside>
      
      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div className="admin-overlay" onClick={handleOverlayClick}></div>
      )}
      
      {/* Content area */}
      <div className="admin-content">
        <header className="admin-header">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button className="mobile-menu-toggle" onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
              <MenuIcon />
            </button>
            <h1 className="page-title">{getPageTitle()}</h1>
          </div>
          
          <div className="user-info">
            <div style={{ marginRight: '0.75rem' }}>
              <AskAiAssistant />
            </div>

            <div style={{ marginRight: '0.75rem' }}>
              <LanguageSwitcher />
            </div>

            <NotificationIcon />

            <div className="user-name">
              {currentUser?.firstName} {currentUser?.lastName}
            </div>
            
            <div className="user-menu">
              <button 
                className="user-menu-button" 
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              >
                {getUserInitials()}
              </button>
              
              {isUserMenuOpen && (
                <div className="user-menu-dropdown">
                  <button className="user-menu-item" onClick={() => navigate('/admin/profile')}>
                    {t('nav.admin.profile')}
                  </button>
                  <button className="user-menu-item" onClick={() => navigate('/admin/settings')}>
                    {t('nav.admin.settings')}
                  </button>
                  <button className="user-menu-item" onClick={() => navigate('/admin/notification-preferences')}>
                    {t('admin.userMenu.notificationSettings')}
                  </button>
                  <button className="user-menu-item logout" onClick={handleLogout}>
                    {t('common.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        
        <main className="main-content">
          <Outlet />
        </main>

        <AppFooter />
      </div>
    </div>
  );
};

export default AdminLayout;
